import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RecoverRequest } from '../../models/auth/recover-request';
import { ConfirmRecoverRequest } from '../../models/auth/confirm-recover-request';

declare global {
  interface Window {
    credentials: {
      save: (credentials: { username: string, password: string }) => void;
      delete: (username: string) => void;
      get: () => [{ username: string, password: string }];
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class IdentityService {

  constructor(
    private httpClient: HttpClient
  ) { }

  /**
   * Returns the current claims within the token.
   * @returns 
   */
  public getClaims() {
    let tokenStr = sessionStorage.getItem('token')
    if (!tokenStr)
      return null;

    try {
      let token = JSON.parse(tokenStr);
      let base64Url = token.token.split('.')[1];
      let decodedValue = JSON.parse(window.atob(base64Url));

      return decodedValue;
    } catch (error) {
      console.error(error);
      return null;
    }

  }

  /*
   * Retrieves all roles associated with the currently authenticated user
   * from the token claims. Supports array or string claims under standard role keys.
   */
  public getUserRoles(): string[] {
    const claims = this.getClaims();
    if (!claims) return [];

    // Extract roles from common claim locations
    const rawRoles = claims.role ?? claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? claims.roles;
    if (!rawRoles) return [];

    if (Array.isArray(rawRoles)) {
      return rawRoles.map((r: any) => String(r).trim());
    }

    if (typeof rawRoles === 'string') {
      return rawRoles.split(',').map(r => r.trim()).filter(Boolean);
    }

    return [];
  }

  /*
   * Checks whether the current user has at least one of the required roles.
   */
  public hasRole(roles: string | string[]): boolean {
    if (!roles) return false;

    // Convert single string or array into normalized role list
    const requiredRoles = Array.isArray(roles)
      ? roles
      : roles.split(',').map(r => r.trim());

    if (requiredRoles.length === 0) return false;

    const userRoles = this.getUserRoles().map(r => r.toLowerCase());
    return requiredRoles.some(role => userRoles.includes(role.trim().toLowerCase()));
  }


  /**
   * Provide http login subscription.
   * @param credentials 
   * @returns 
   */
  login(credentials: any): Observable<any> {
    return this.httpClient.post(environment.oAuthApiUrl + "api/oauth/token", credentials, { withCredentials: true });
  }

  /**
   * Register a new user account.
   * @param model 
   * @returns 
   */
  register(model: any): Observable<any> {
    const payload = {
      UserName: model.username || model.UserName,
      Password: model.password || model.Password,
      ConfirmPassword: model.confirmPassword || model.ConfirmPassword,
      FirstName: model.firstName || model.FirstName,
      LastName: model.lastName || model.LastName,
      Email: model.email || model.Email,
      PhoneNumber: model.phoneNumber || model.PhoneNumber
    };
    return this.httpClient.post(environment.oAuthApiUrl + "api/account/register", payload);
  }

  /**
   * Begin account recovery.
   * @param request 
   * @returns 
   */
  requestAccountRecovery(request: RecoverRequest) {
    return this.httpClient.post(environment.oAuthApiUrl + "api/account/password/reset", request);
  }

  /**
   * Send reset password confirmation
   * contains username, passcode and new password.
   * @param request 
   * @returns 
   */
  resetPassword(request: ConfirmRecoverRequest) {
    return this.httpClient.post(environment.oAuthApiUrl + "api/account/password/confirm", request);
  }

  /**
   * Removes the token from session storage.
   */
  clearToken() {
    sessionStorage.removeItem("token");
  }

  getToken() {
    const tokenStr = sessionStorage.getItem("token");
    if (tokenStr)
      return JSON.parse(tokenStr);
    else
      return null;
  }

  /**
   * Store the token in the session storage.
   * @param token 
   */
  storeToken(token: any) {
    sessionStorage.setItem("token", JSON.stringify(token));
  }

  /**
   * Returns all remembered accounts.
   */
  getRememberedAccounts(): any[] {
    if (window.credentials)
      return window.credentials.get();
    else
      return [];
  }

  /**
   * Saves a username/password pair to remembered accounts.
   * If the username already exists, the password is updated.
   */
  rememberAccount(username: string, password: string): void {
    if (window.credentials)
      window.credentials.save({ username, password });
  }

  /**
   * Removes a remembered account by username.
   */
  forgetAccount(username: string): void {
    if (window.credentials)
      window.credentials.delete(username);
  }
}
